import React, { useEffect, useMemo, useRef, useState } from 'react';
import Layout from '@shared/ui/organisms/layout/Layout';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { HeadlineMedium } from '@shared/ui/atoms/fonts/Headline';
import { BodyRegular } from '@shared/ui/atoms/fonts/Body';
import { GoBackButton } from '@shared/ui/atoms/buttons/GoBackButton';
import { useDispatch, useSelector } from 'react-redux';
import Icon, { IconTypes } from '@shared/ui/atoms/icon/Icon';
import { tablet } from '@shared/ui/atoms/MediaQueries';
import Colours from '@shared/ui/atoms/Colours';
import { selectCurrentUser } from '@shared/store/auth.duck';
import { canUserUpdateCompanySettings } from '@shared/utils/AccessControl';
import { Permission } from '@shared/models/user.models';
import { getParameterByName, setQueryParams } from '@shared/utils/UrlUtils';
import AccessRequired from '../components/AccessRequired';
import { reset } from '../edit-company-page/editCompany.duck';
import { SidebarItem } from '../office-management-page-v2/components/SidebarItem';
import { CompanyInformationView } from './data/CompanyManagement.model';
import { resetEditCompanyLayout, selectActivePanel, setActivePanel, } from './data/editCompanyLayout.duck';
import { GeneralPanel } from './components/GeneralPanel';
import { NotificationsPanel } from './components/NotificationsPanel';
import { SignupRegisterPanel } from './components/SignupRegisterPanel';
import { AzureActiveDirectoryPanel } from './components/AzureActiveDirectoryPanel';
import { ApiKeysPanel } from './components/ApiKeysPanel';
import { VisitorCheckinKioskPanel } from './components/VisitorCheckinKioskPanel';
import { IsBetaTester } from '@shared/hooks/IsBetaTester';
import { OtherPanel } from './components/OtherPanel';
import { DangerZonePanel } from './components/DangerZonePanel';
import { WhereaboutsVisibilityPanel } from './components/WhereaboutsVisibilityPanel';
import { AutomatedReportingPanel } from './components/AutomatedReportingPanel';

export function CompanyManagementPageV2() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const user = useSelector(selectCurrentUser);
  const activePanel = useSelector(selectActivePanel);
  const editable = user?.role === Permission.CompanyAdmin;
  const isEditable = useMemo(() => canUserUpdateCompanySettings(user), [user]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const company = user?.companyEntity;

  const menuItems = useMemo(() => [
    { view: CompanyInformationView.General, icon: IconTypes.Settings, label: 'company-settings-page.general', visible: true },
    { view: CompanyInformationView.Notifications, icon: IconTypes.Notification, label: 'company-settings-page.notifications-tab', visible: true },
    { view: CompanyInformationView.WhereaboutsVisibility, icon: IconTypes.Users, label: 'company-settings-page.whereabouts-management-tab', visible: true },
    { view: CompanyInformationView.SignupRegister, icon: IconTypes.Profile, label: 'company-settings-page.signup-and-register-tab', visible: true },
    { view: CompanyInformationView.AutomatedReporting, icon: IconTypes.Reporting, label: 'company-settings-page.automated-reporting-tab', visible: true },
    { view: CompanyInformationView.AzureActiveDirectory, icon: IconTypes.Reporting, label: 'company-settings-page.azure-configuration', visible: true },
    { view: CompanyInformationView.ApiKeys, icon: IconTypes.Padlock, label: 'company-settings-page.api-keys-tab', visible: editable },
    { view: CompanyInformationView.VisitorCheckin, icon: IconTypes.Profile, label: 'company-settings-page.visitors-tab', visible: editable },
    { view: CompanyInformationView.Other, icon: IconTypes.Settings, label: 'company-settings-page.other-tab', visible: true },
    { view: CompanyInformationView.DangerZone, icon: IconTypes.Info, label: 'company-settings-page.danger-zone', visible: true },
  ].filter(item => item.visible), [editable]);

  const activeItem = menuItems.find(item => item.view === activePanel) ?? menuItems[0];

  useEffect(() => {
    dispatch(reset());
    const qsPanel = getParameterByName('panel', window.location.href);
    if (qsPanel) {
      dispatch(setActivePanel(parseInt(qsPanel)));
    }
    return () => {
      dispatch(reset());
      dispatch(resetEditCompanyLayout());
    };
  }, [dispatch]);

  const initialLoad = useRef(true);
  useEffect(() => {
    if (initialLoad.current) {
      initialLoad.current = false;
      return;
    }
    setQueryParams([`panel=${activePanel}`]);
  }, [activePanel]);

  const onPanelChange = (panel: CompanyInformationView) => {
    dispatch(setActivePanel(panel));
    setMobileMenuOpen(false);
  };

  if (!company) {
    return null;
  }

  return (
    <Layout fullWidth={true} hideFooter={true}>
      <RibbonContainer>
        <RibbonContentContainer>
          <GoBackButton url={'/manage'} />
          <HeadlineMedium>{t('company-settings-page.company-settings')}</HeadlineMedium>
        </RibbonContentContainer>
      </RibbonContainer>

      {isEditable && (
        <ContentContainer>
          <Sidebar>
            <MobileMenuToggle onClick={() => setMobileMenuOpen(open => !open)}
                              aria-expanded={mobileMenuOpen}
                              type="button">
              <MobileMenuToggleLabel>
                <Icon icon={activeItem.icon} />
                <BodyRegular weight={600}>{t(activeItem.label)}</BodyRegular>
              </MobileMenuToggleLabel>
              <Icon icon={mobileMenuOpen ? IconTypes.Up : IconTypes.Down} />
            </MobileMenuToggle>
            <SidebarItems $open={mobileMenuOpen}>
              {menuItems.map(item => (
                <SidebarItem key={item.view}
                             icon={item.icon}
                             label={item.label}
                             active={activePanel === item.view}
                             onClick={() => onPanelChange(item.view)} />
              ))}
            </SidebarItems>
          </Sidebar>
          <MainContent>
            <AccessRequired visible={!editable} companyAdminsRequired={true} />
            {activePanel === CompanyInformationView.General && <GeneralPanel editable={editable} />}
            {activePanel === CompanyInformationView.Notifications && <NotificationsPanel />}
            {activePanel === CompanyInformationView.WhereaboutsVisibility && <WhereaboutsVisibilityPanel />}
            {activePanel === CompanyInformationView.SignupRegister && <SignupRegisterPanel />}
            {activePanel === CompanyInformationView.AzureActiveDirectory && <AzureActiveDirectoryPanel />}
            {activePanel === CompanyInformationView.ApiKeys && editable && <ApiKeysPanel />}
            {activePanel === CompanyInformationView.VisitorCheckin && editable && <VisitorCheckinKioskPanel />}
            {activePanel === CompanyInformationView.AutomatedReporting && <AutomatedReportingPanel />}
            {activePanel === CompanyInformationView.Other && <OtherPanel />}
            {activePanel === CompanyInformationView.DangerZone && <DangerZonePanel editable={editable} />}
          </MainContent>
        </ContentContainer>
      )}
    </Layout>
  );
}

const Sidebar = styled.div`
  width: 100%;
  @media (${tablet}) {
    width: 220px;
    min-width: 220px;
    max-width: 220px;
    margin: 0;
  }
`

const MobileMenuToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 14px 16px;
  background: white;
  border: none;
  border-bottom: 1px solid ${Colours.lightGrey};
  cursor: pointer;
  i {
    margin-right: 10px;
  }
  @media (${tablet}) {
    display: none;
  }
`

const MobileMenuToggleLabel = styled.div`
  display: flex;
  align-items: center;
`

const SidebarItems = styled.div<{ $open: boolean }>`
  display: ${props => props.$open ? 'block' : 'none'};
  padding: 16px 16px 12px 16px;
  @media (${tablet}) {
    display: block;
    padding: 0;
  }
`

const MainContent = styled.div`
  flex: 1;
  min-height: 500px;
  background-color: white;
  padding: 32px 16px 0 16px;
  @media (${tablet}) {
    padding: 48px 120px;
  }
`

const ContentContainer = styled.div`
  max-width: 1400px;
  background-color: ${Colours.veryLightGrey};
  @media (${tablet}) {
    padding: 0 16px;
    background-color: unset;
    display: flex;
    margin: 24px auto 0 auto;
  }
`

const RibbonContainer = styled.div`
  background-color: white;
  padding: 0 16px;
  box-shadow: 0 9px 22px 0 rgba(46, 74, 148, 0.08);
`

const RibbonContentContainer = styled.div`
  width: 100%;
  margin: 0 auto;
  max-width: 1400px;
  padding: 16px 0;
  @media (${tablet}) {
    padding: 16px 0;
  }
`
